import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { AlertCircle, Check, CheckCircle2, ExternalLink } from "lucide-react";
import { PublicShell } from "@/components/layout/PublicShell";
import { SEO } from "@/lib/seo";
import { Button, ButtonLink, EmptyState, Panel, Status, TextAreaField } from "@/components/system";
import { useCity } from "@/contexts/CityContext";
import {
  ALERT_CATEGORY_LABELS,
  ALERT_SEVERITY_LABELS,
  formatRelativeTime,
  useActionAlert,
  useAlertDetail,
  useMarkAlertRead,
} from "@/lib/alerts";

/**
 * Alert detail page. The opening GET implicitly marks the alert read
 * (optimistic update + background POST). The "I actioned this" affordance
 * below is separate — represents a clinical act (updated patient recall,
 * ran a prescription audit, etc.) and takes an optional free-text note
 * for audit trail.
 */
export default function AlertDetail() {
  const { id } = useParams<{ id: string }>();
  const { city, link } = useCity();
  const { data, isLoading, isError, error } = useAlertDetail(city, id);
  const markRead = useMarkAlertRead(city);
  const actionAlert = useActionAlert(city);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);

  // Mark read on first render if not already read.
  useEffect(() => {
    if (data && !data.read_at && id) {
      markRead.mutate(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  if (!id) return <Navigate to={link("/alerts")} replace />;

  return (
    <PublicShell>
      <SEO
        title={
          data ? `${data.title} · Alerts · ${city.name} · PulseBoard` : "Alert"
        }
        description={data?.summary ?? "Public health alert"}
        path={`/alerts/${id}`}
        noindex
      />

      <div className="ds-container ds-container--narrow">
        {isLoading ? (
          <div className="ds-section"><div className="ds-skeleton" style={{ height: 320 }} aria-label="Loading alert" /></div>
        ) : isError || !data ? (
          <div className="ds-section">
            <EmptyState icon={<AlertCircle />} title="This alert isn't available" action={<ButtonLink to={link("/alerts")}>Back to alerts</ButtonLink>}>
              {error instanceof Error ? error.message : "It may have been withdrawn or the link may be out of date."}
            </EmptyState>
          </div>
        ) : (
          <article>
            <header className="ds-page-head">
              <ol className="ds-crumbs" aria-label="Breadcrumb">
                <li><Link to={link("/alerts")}>Alerts</Link></li>
              </ol>
              <div className="ds-cluster" style={{ gap: 10 }}>
                <Status tone={severityLevel(data.severity) >= 4 ? "urgent" : severityLevel(data.severity) === 3 ? "warn" : "neutral"}>
                  {ALERT_SEVERITY_LABELS[data.severity]}
                </Status>
                <span className="ds-eyebrow">{ALERT_CATEGORY_LABELS[data.category]}</span>
                <span className="ds-fine">{formatRelativeTime(data.published_at)}</span>
              </div>
              <h1 className="ds-display-1" style={{ fontSize: "clamp(32px, 4.4vw, 48px)" }}>{data.title}</h1>
              {data.summary ? <p className="ds-lead">{data.summary}</p> : null}
            </header>

            <div className="ds-stack" style={{ gap: 16 }}>
              <Panel as="div">
                <div className="ds-body" style={{ whiteSpace: "pre-wrap", maxWidth: "none" }}>{data.body_markdown}</div>
                {data.source_url ? (
                  <a className="ds-link ds-small" href={data.source_url} target="_blank" rel="noopener noreferrer">
                    View official source <ExternalLink aria-hidden="true" className="ds-inline-icon" />
                  </a>
                ) : null}
                {data.valid_until ? (
                  <p className="ds-fine">
                    Valid until{" "}
                    <time dateTime={data.valid_until}>
                      {new Date(data.valid_until).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
                    </time>
                  </p>
                ) : null}
              </Panel>

              <Panel tone="sunken" tight as="div">
                {data.actioned_at ? (
                  <div className="ds-stack" style={{ gap: 4 }}>
                    <p className="ds-title" style={{ fontSize: 17 }}>
                      <CheckCircle2 aria-hidden="true" className="ds-inline-icon" /> You actioned this {formatRelativeTime(data.actioned_at)}
                    </p>
                    <p className="ds-small">Visible only to you, for your own records.</p>
                  </div>
                ) : (
                  <>
                    <div className="ds-stack" style={{ gap: 4 }}>
                      <h2 className="ds-title" style={{ fontSize: 17 }}>Did you act on this?</h2>
                      <p className="ds-small">
                        Mark it actioned for your own records, for example after updating a recall list or running a prescribing audit.
                      </p>
                    </div>
                    {showNote ? (
                      <div className="ds-stack" style={{ gap: 12 }}>
                        <TextAreaField
                          label="Note"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          maxLength={2000}
                          rows={3}
                          placeholder="Optional, for example: sent SMS to at-risk patient list"
                        />
                        <div className="ds-cluster">
                          <Button disabled={actionAlert.isPending} onClick={() => actionAlert.mutate({ id, note: note || undefined })}>
                            <Check aria-hidden="true" />
                            Mark actioned
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setShowNote(false);
                              setNote("");
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div><Button variant="secondary" onClick={() => setShowNote(true)}>Mark actioned</Button></div>
                    )}
                  </>
                )}
              </Panel>

              {data.linked_tool_ids.length > 0 || data.linked_thread_id ? (
                <div className="ds-stack" style={{ gap: 8 }}>
                  <p className="ds-eyebrow">Related</p>
                  <ul className="ds-listing__tags">
                    {data.linked_tool_ids.map((toolId) => <li key={toolId} className="ds-tag">Tool · {toolId.slice(0, 8)}</li>)}
                    {data.linked_thread_id ? <li className="ds-tag">Forum thread</li> : null}
                  </ul>
                </div>
              ) : null}
            </div>
          </article>
        )}
      </div>
    </PublicShell>
  );
}

function severityLevel(severity: "info" | "watch" | "advisory" | "warning" | "critical"): 1 | 2 | 3 | 4 {
  if (severity === "critical" || severity === "warning") return 4;
  if (severity === "advisory") return 3;
  if (severity === "watch") return 2;
  return 1;
}
